import { useState } from 'react';
import { Box, Button, CircularProgress, Fade, Grid2, Typography } from '@mui/material';
import { ExperienceStatus, formatCurrency, timeDurationFormatter } from '@utriper/core';
import { Chip, PreviewPriceGroup } from '@utriper/core-ui';
import { useTranslation } from 'react-i18next';
import CopyButton from '../CopyButton';
import { AccessTime, Edit, Inventory2Outlined, TaskAlt } from '@mui/icons-material';
import { useExperienceDetail } from 'core/contexts/experienceDetail';
import { PreviewService } from 'core/api/services/preview';
import { toast } from 'react-toastify';

const ExperienceDetailCard = ({ experienceId, experienceDetail, handleEdit = () => {} }) => {
    const { t: translate, i18n } = useTranslation();
    const { updateExperienceDetail } = useExperienceDetail();

    const [updating, setUpdating] = useState(false);

    const hasPrice = experienceDetail?.currency && Number.isFinite(experienceDetail?.price) ? true : false;
    const hasPriceGroup = Number.isInteger(experienceDetail?.priceGroupId) && experienceDetail.priceGroupId > 0;

    const renderStatus = (status) => {
        let color = '';
        const statusTranslated = ExperienceStatus.getTranslatedName(ExperienceStatus.status, status, i18n.language);

        switch (status) {
            case ExperienceStatus.IDS.live:
                color = '#50C878';
                break;
            case ExperienceStatus.IDS.draft:
                color = '#9EC4D6';
                break;
            case ExperienceStatus.IDS.archived:
                color = '#131313bf';
                break;
            default:
                color = '#50C878';
                break;
        }

        return <Chip label={statusTranslated} sx={{ color, backgroundColor: `${color}40`, borderRadius: '4px', fontWeight: '600' }} />;
    };

    const handleStatus = async (status) => {
        setUpdating(true);
        try {
            if (!experienceId || !experienceDetail?.id) {
                throw new Error(translate('experience_detail_card.toasts.errors.no_valid_option'));
            }

            if (status === experienceDetail?.status) {
                throw new Error(translate('experience_detail_card.toasts.errors.no_valid_status'));
            }

            const values = {
                status,
            };

            const result = await updateExperienceDetail(experienceId, experienceDetail?.id, values);

            if (!result) {
                throw new Error(translate('experience_detail_card.toasts.errors.update_failed'));
            } else {
                toast.success(translate('experience_detail_card.toasts.success.status_updated'), {
                    position: 'top-right',
                    theme: 'colored',
                });
            }
        } catch (error) {
            toast.error(error?.message || error, {
                position: 'top-right',
                theme: 'colored',
            });
        } finally {
            setUpdating(false);
        }
    };

    return (
        <Fade in={true} timeout={400}>
            <Grid2
                size={4}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    gap: '1.5em',
                    padding: '1em',
                    borderRadius: '.5em',
                    border: 'solid #13131340 1px',
                    boxShadow: 'rgba(99, 99, 99, 0.3) 0px 2px 8px 0px',
                }}
            >
                <Grid2 sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Typography sx={{ color: '#131313', fontSize: '1.2em', fontWeight: '600' }}>{experienceDetail?.description1}</Typography>
                    {renderStatus(experienceDetail?.status)}
                </Grid2>
                <Grid2 container sx={{ width: '100%' }}>
                    {experienceDetail?.description2 && (
                        <Typography
                            sx={{
                                color: '#131313bf',
                                width: '100%',
                                fontSize: '1em',
                                lineHeight: 1.5,
                                overflow: 'hidden',
                                display: '-webkit-box',
                                WebkitBoxOrient: 'vertical',
                                WebkitLineClamp: 3,
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {experienceDetail.description2}
                        </Typography>
                    )}
                </Grid2>
                <Grid2 sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Grid2
                        sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block',
                            maxWidth: 'calc(100% - 52px)',
                        }}
                    >
                        <Typography noWrap sx={{ color: '#131313bf', fontSize: '1em' }}>
                            <span style={{ fontWeight: '600' }}>{`${translate('experience_detail_card.id')}: `}</span>
                            {experienceDetail?.id}
                        </Typography>
                    </Grid2>
                    <CopyButton textToCopy={experienceDetail?.id} />
                </Grid2>
                {/* <Grid2 sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Grid2
                        sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block',
                            maxWidth: 'calc(100% - 52px)',
                        }}>
                        <Typography noWrap sx={{ color: '#131313bf', fontSize: '1em' }}>
                            <span style={{ fontWeight: '600' }}>{`${translate('experience_detail_card.code')}: `}</span>
                            {experienceDetail?.code}
                        </Typography>
                    </Grid2>
                    <CopyButton textToCopy={experienceDetail?.code} />
                </Grid2> */}
                <Grid2 sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                    {(experienceDetail?.duration === 0 || experienceDetail?.duration) && (
                        <Grid2 container justifyContent="space-between" alignItems="center" gap="8px" flexWrap="nowrap">
                            <AccessTime sx={{ color: '#131313bf' }} />
                            <Typography sx={{ color: '#131313bf', fontWeight: '600', fontSize: '1em' }}>{experienceDetail?.duration === 0 ? '0' : timeDurationFormatter(experienceDetail?.duration, translate)}</Typography>
                        </Grid2>
                    )}
                    {hasPriceGroup ? <PreviewPriceGroup id={experienceDetail.priceGroupId} label="name" previewService={PreviewService} route={`/price-group/${experienceDetail.priceGroupId}`} /> : <Typography sx={{ color: hasPrice ? '#137DC5' : '#131313bf', fontWeight: '600', fontSize: hasPrice ? '1.125em' : '1em' }}>{hasPrice ? formatCurrency(experienceDetail.price, experienceDetail.currency, i18n.language) : translate('experience_detail_card.auto')}</Typography>}
                </Grid2>
                <Grid2 sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1em', marginTop: 'auto' }}>
                    <Button
                        onClick={() => handleEdit(experienceDetail)}
                        sx={{
                            fontWeight: '600',
                            width: '100%',
                            color: '#131313',
                            textTransform: 'none',
                            border: 'solid #13131340 1px',
                        }}
                        variant="outlined"
                        startIcon={<Edit sx={{ color: '#131313' }} />}
                    >
                        {translate('experience_detail_card.edit')}
                    </Button>
                    {experienceDetail?.status !== ExperienceStatus.IDS.live ? (
                        <Button
                            onClick={() => handleStatus(ExperienceStatus.IDS.live)}
                            sx={{
                                fontWeight: '600',
                                width: '100%',
                                color: 'white',
                                textTransform: 'none',
                                backgroundColor: '#131313',
                            }}
                            variant="contained"
                            loading={updating}
                            loadingIndicator={
                                <Box sx={{ display: 'flex' }}>
                                    <CircularProgress size={20} sx={{ color: '#131313bf' }} />
                                </Box>
                            }
                            startIcon={!updating && <TaskAlt sx={{ color: 'white' }} />}
                        >
                            {translate('experience_detail_card.publish')}
                        </Button>
                    ) : (
                        <Button
                            onClick={() => handleStatus(ExperienceStatus.IDS.archived)}
                            sx={{
                                fontWeight: '600',
                                width: '100%',
                                color: '#131313',
                                textTransform: 'none',
                                backgroundColor: '#13131326',
                            }}
                            variant="contained"
                            loading={updating}
                            loadingIndicator={
                                <Box sx={{ display: 'flex' }}>
                                    <CircularProgress size={20} sx={{ color: '#131313bf' }} />
                                </Box>
                            }
                            startIcon={!updating && <Inventory2Outlined sx={{ color: '#131313' }} />}
                        >
                            {translate('experience_detail_card.archive')}
                        </Button>
                    )}
                </Grid2>
            </Grid2>
        </Fade>
    );
};

export default ExperienceDetailCard;
